import { useState } from "react";
import { Fingerprint } from "lucide-react";
import { PortalLayout } from "@/components/layout/PortalLayout";
import { GlassCard } from "@/components/GlassCard";
import { inputCls, labelCls, goldBtnSm } from "@/lib/constants";

async function sha256Hex(str) {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(str));
  return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join("");
}

export default function SecretFingerprint() {
  const [value, setValue] = useState("");
  const [fp, setFp] = useState(null);
  const [counterparty, setCounterparty] = useState("");

  const compute = async () => {
    if (!value) return;
    const full = await sha256Hex(value);
    setFp({ full, short: full.slice(0, 12), length: value.length });
  };

  const match = fp && counterparty && counterparty.trim().toLowerCase() === fp.short.toLowerCase();

  return (
    <PortalLayout title="Secret Fingerprint">
      <p className="text-sm text-muted-foreground mb-6 max-w-2xl">
        Compute a secret&rsquo;s SHA-256 fingerprint locally, in your browser. The value is never transmitted &mdash;
        use this to reconcile with a counterparty without sharing the raw secret.
      </p>
      <GlassCard className="p-6 max-w-2xl">
        <label className={labelCls}>Secret value (stays in your browser)</label>
        <input type="password" value={value} onChange={(e) => setValue(e.target.value)} data-testid="fingerprint-input" className={inputCls} placeholder="Paste secret to fingerprint" />
        <button onClick={compute} data-testid="fingerprint-compute" className={goldBtnSm + " mt-4"}><Fingerprint size={13} /> Compute fingerprint</button>
        {fp && (
          <div className="mt-6 space-y-2">
            <div className="text-sm text-foreground">First 12 hex: <span className="pdg-mono text-[hsl(var(--pdg-gold))]" data-testid="fingerprint-result">{fp.short}</span></div>
            <div className="text-xs text-muted-foreground pdg-mono break-all">Full: {fp.full}</div>
            <div className="text-xs text-muted-foreground">Length: {fp.length}</div>
            <div className="mt-4">
              <label className={labelCls}>Counterparty fingerprint (first 12 hex)</label>
              <input value={counterparty} onChange={(e) => setCounterparty(e.target.value)} className={inputCls} placeholder="e.g. 4adde5aba53d" />
              {counterparty && (
                <div className={`mt-3 text-sm ${match ? "text-[hsl(145_38%_66%)]" : "text-[hsl(0_45%_70%)]"}`}>
                  {match ? "✓ Fingerprints match — secrets are in sync." : "✗ Mismatch — secret drift detected."}
                </div>
              )}
            </div>
          </div>
        )}
      </GlassCard>
    </PortalLayout>
  );
}
